import React, { useState } from 'react';
import { hostProfileApi } from '../services/hostProfileApi.ts';
import { HostWithStats } from '../services/hostService.ts';

interface HostPipelineTabProps {
  selectedHost: HostWithStats | null;
  onSelectHostById?: (hostId: string) => void;
}

export const HostPipelineTab: React.FC<HostPipelineTabProps> = ({ selectedHost, onSelectHostById }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [discoverResult, setDiscoverResult] = useState<Awaited<ReturnType<typeof hostProfileApi.discover>> | null>(null);
  const [exactResult, setExactResult] = useState<Awaited<ReturnType<typeof hostProfileApi.discoverExact>> | null>(null);
  const [candidatePreview, setCandidatePreview] = useState<Record<string, unknown> | null>(null);
  const [sampleSize, setSampleSize] = useState(4000);
  const [limitDiscover, setLimitDiscover] = useState(20);
  const [limitExact, setLimitExact] = useState(50);

  const runDiscover = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await hostProfileApi.discover({ sample_size: sampleSize, limit: limitDiscover });
      setDiscoverResult(r);
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const runExact = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await hostProfileApi.discoverExact({ sample_size: sampleSize, limit: limitExact });
      setExactResult(r);
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const previewCandidate = async (place: string, source: string, eventUrl: string) => {
    setBusy(true);
    setError(null);
    setCandidatePreview(null);
    try {
      const p = await hostProfileApi.preview({
        place,
        source,
        event_url: eventUrl,
        sample_events: 5,
      });
      setCandidatePreview(p);
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="host-pipeline-tab">
      <section className="host-pipeline-section">
        <h3>Selected host</h3>
        {selectedHost ? (
          <p>
            <strong>{selectedHost.title || selectedHost.id}</strong> ·{' '}
            <code>{selectedHost.id}</code> — use the <strong>Hosts Table</strong> tab and open the detail panel for
            pipeline buttons (preview, upsert, link-overrides).
          </p>
        ) : (
          <p className="hosts-missing">Select a host row on the Hosts Table tab to run per-host actions in the side panel.</p>
        )}
      </section>

      <section className="host-pipeline-section">
        <h3>Discover (server)</h3>
        <p className="host-pipeline-hint">
          Calls <code>discover_unmapped_host_candidates</code> and <code>discover_exact_existing_host_matches</code> on
          the API (not the lightweight client sample used on the Audit tab).
        </p>
        <div className="host-pipeline-controls">
          <label>
            Sample size{' '}
            <input
              type="number"
              min={100}
              max={20000}
              value={sampleSize}
              onChange={(e) => setSampleSize(Number(e.target.value))}
            />
          </label>
          <label>
            Unmapped limit{' '}
            <input
              type="number"
              min={1}
              max={100}
              value={limitDiscover}
              onChange={(e) => setLimitDiscover(Number(e.target.value))}
            />
          </label>
          <label>
            Exact-match limit{' '}
            <input
              type="number"
              min={1}
              max={200}
              value={limitExact}
              onChange={(e) => setLimitExact(Number(e.target.value))}
            />
          </label>
        </div>
        <div className="host-pipeline-actions">
          <button type="button" className="hosts-refresh-btn" disabled={busy} onClick={runDiscover}>
            {busy ? 'Running…' : 'Run discover (unmapped)'}
          </button>
          <button type="button" className="hosts-refresh-btn" disabled={busy} onClick={runExact}>
            {busy ? 'Running…' : 'Run discover (exact matches)'}
          </button>
        </div>
      </section>

      {error && <div className="host-profile-error">{error}</div>}

      {discoverResult && (
        <section className="host-pipeline-section">
          <h4>
            Unmapped candidates ({discoverResult.candidates?.length ?? 0}) · sampled {discoverResult.sampled_posts} posts
          </h4>
          <div className="host-pipeline-table-wrap">
            <table className="host-audit-unmapped-table">
              <thead>
                <tr>
                  <th>Place</th>
                  <th>Count</th>
                  <th>Source</th>
                  <th>Preview</th>
                </tr>
              </thead>
              <tbody>
                {(discoverResult.candidates || []).map((c, i) => (
                  <tr key={i}>
                    <td>{c.place}</td>
                    <td>{c.count}</td>
                    <td>{c.source || '—'}</td>
                    <td>
                      <button
                        type="button"
                        className="hosts-refresh-btn"
                        disabled={busy}
                        onClick={() => previewCandidate(c.place, c.source || '', c.event_url || '')}
                      >
                        Preview
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {exactResult && (
        <section className="host-pipeline-section">
          <h4>
            Exact title/alias matches ({exactResult.matches?.length ?? 0}) · scanned {exactResult.sampled_posts} posts
          </h4>
          <div className="host-pipeline-table-wrap">
            <table className="host-audit-unmapped-table">
              <thead>
                <tr>
                  <th>Host</th>
                  <th>Posts</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {(exactResult.matches || []).map((m, i) => (
                  <tr key={i}>
                    <td>
                      {m.host_title} <code className="host-pipeline-code">{m.host_id}</code>
                    </td>
                    <td>{m.count}</td>
                    <td>
                      {onSelectHostById ? (
                        <button
                          type="button"
                          className="hosts-refresh-btn"
                          onClick={() => onSelectHostById(m.host_id)}
                        >
                          Focus host (table)
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {candidatePreview && (
        <section className="host-pipeline-section">
          <h4>Candidate preview (not saved)</h4>
          <pre className="host-pipeline-json">{JSON.stringify(candidatePreview, null, 2)}</pre>
        </section>
      )}
    </div>
  );
};
